/**
 * ImageGenerationStep.tsx
 *
 * Build Bar — Step 3: Imágenes.
 *
 * Guides the user through slides that need images (determined by
 * `componentNeedsImage()` from the slide component catalog, NOT by
 * `slide.images[]` which may be empty before backend endpoints exist).
 *
 * Key features:
 *
 *  - Toolbar: "Generar todas" (fires all idle slots in parallel), default
 *    provider radio, and a counter showing how many slots have been completed.
 *  - Left sidebar: scrollable list of slide thumbnails with status badges.
 *    Slides that don't need images are dimmed. Click to select.
 *  - Center-right panel (for the selected slide):
 *      • Prompt textarea — auto-generated from slide content, freely editable.
 *      • Reference images strip — horizontal chips with "x" to remove +
 *        "[+ Agregar referencia]" button that opens AddReferenceModal.
 *      • AI Assist input — calls usePromptAssist to rewrite the prompt.
 *      • Chroma background checkbox — injects a green-screen instruction.
 *      • Provider radio — Gemini / OpenAI (per-slot override).
 *      • Generate / Regenerate buttons — call useGenerateImage.
 *      • Preview area — shows the generated image or a skeleton while loading.
 *      • Background-removal buttons — PIL (fast) and AI (better quality).
 *  - Footer: "Continuar a costos" — enabled when all slots are done or skipped.
 *
 * Layout contract (NO page scroll — internal panels scroll):
 *
 *   ┌─ .container ──────────────────────────────────────────────────────┐
 *   │  .toolbar (flex-shrink: 0)                                        │
 *   │  .editorArea (flex: 1, min-height: 0)                             │
 *   │    .sidebar (200px, overflow-y: auto)                             │
 *   │    .centerPanel (flex: 1, overflow-y: auto)                       │
 *   │  .footerRow (flex-shrink: 0)                                      │
 *   └───────────────────────────────────────────────────────────────────┘
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui';
import { extractApiError } from '@/lib/axios';
import { useWizardStore } from '@/stores/useWizardStore';
import {
  useImagePrompts,
  useGenerateImage,
  useRemoveBackground,
  usePromptAssist,
  useSaveSlides,
  useBaseProducts,
} from '@/api/useWizardV2';
import { componentNeedsImage, componentImageCount, getComponentLabel } from '@/constants/slideComponents';
import { generateRawPrompt, getImageStyle, getImageAspectRatio } from '@/constants/imagePrompts';
import { EQUIPMENT_SLUG_MAP } from '@/constants/defaultDeck';
import { usePlantillas } from '@/api/usePresentationWizard';
import type { ReferenceImage, SlideConfig } from '@/types/wizardV2';
import { SlideMockPreview } from './SlideMockPreview';
import { SlideLightbox } from './SlideLightbox';
import { AddReferenceModal } from './AddReferenceModal';
import { AiAssistInput } from '../components/AiAssistInput';
import styles from './ImageGenerationStep.module.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const SIDEBAR_SKELETON_COUNT = 8;

const IMAGE_GEN_LABELS = [
  'Calling API for generation',
  'Considering prompt',
  'Adding brand elements',
  'Finalizing render',
];

const CHROMA_INSTRUCTION =
  ' Render the subject on a solid bright green (#00FF00) chroma key background for compositing.';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Derive a stable slot key from slide index + image index.
 * For component-based virtual slots we always use imageIndex 0.
 */
function slotKey(slideIndex: number, imageIndex: number): string {
  return `${slideIndex}-${imageIndex}`;
}

// buildDefaultPrompt removed — now uses generateRawPrompt from @/constants/imagePrompts

// ─── Component ────────────────────────────────────────────────────────────────

export const ImageGenerationStep = () => {
  // ── Store ──────────────────────────────────────────────────────────────────

  const {
    presentationId,
    slides,
    imageSlots,
    referenceImages,
    formData,
    selectedProviders,
    initImageSlot,
    setImageSlotResult,
    setImageSlotGenerating,
    addReferenceImage,
    goToBuildStep,
    selectedSlideIndex,
    setSelectedSlideIndex,
    setSlides,
    removeSlide,
  } = useWizardStore();

  // ── Plantilla backgrounds (same logic as SlideConfigStep) ──────────────────

  const { data: plantillas } = usePlantillas();
  const selectedPlantilla = plantillas?.find((p) => p.slug === formData.plantilla_slug);

  /**
   * Resolve the plantilla background URL for a slide, cycling through
   * backgrounds based on component type + position (hero vs non-hero).
   */
  const getPlantillaBgUrl = useCallback(
    (slide: SlideConfig, slideIndex: number): string | undefined => {
      if (!selectedPlantilla) return undefined;
      const bgs = selectedPlantilla.backgrounds;
      if (!bgs || bgs.length === 0) return selectedPlantilla.preview_image;

      const idx = slide.background?.plantilla_index ?? 0;
      if (idx !== 0) {
        const exact = bgs.find((b) => b.index === idx);
        if (exact) return exact.url;
      }

      const isHero = slide.component.startsWith('hero_');
      const heroBgs = bgs.filter((b) => b.usage === 'hero_only' || b.usage === 'any');
      const anyBgs = bgs.filter((b) => b.usage !== 'hero_only');
      const pool = isHero ? (heroBgs.length > 0 ? heroBgs : bgs) : (anyBgs.length > 0 ? anyBgs : bgs);

      return pool[slideIndex % pool.length]?.url ?? bgs[0]?.url ?? selectedPlantilla.preview_image;
    },
    [selectedPlantilla],
  );

  // ── Image prompts query (may 404 if backend not ready — that's OK) ────────

  const {
    data: promptData,
    isLoading: promptsLoading,
  } = useImagePrompts(presentationId ?? '');

  const { data: baseProducts } = useBaseProducts();

  // ── API mutations ──────────────────────────────────────────────────────────

  const generateImage = useGenerateImage();
  const removeBg = useRemoveBackground();
  const promptAssist = usePromptAssist();

  // ── Local UI state ─────────────────────────────────────────────────────────

  const [defaultProvider, setDefaultProvider] = useState<'gemini' | 'openai'>('gemini');
  const [slotProviders, setSlotProviders] = useState<Record<string, 'gemini' | 'openai'>>({});
  const [slotPrompts, setSlotPrompts] = useState<Record<string, string>>({});
  const [slotChroma, setSlotChroma] = useState<Record<string, boolean>>({});
  const [slotReferenceIds, setSlotReferenceIds] = useState<Record<string, string[]>>({});
  const [refModalOpen, setRefModalOpen] = useState(false);
  const [assistError, setAssistError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    slideIndex: number;
  } | null>(null);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);
  const [imgLabel, setImgLabel] = useState(0);

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', close);
    };
  }, [contextMenu]);

  // ── Slides that need images (derived from component catalog) ──────────────

  /**
   * Set of slide indices whose component type requires at least one image.
   * This replaces the old `slide.images.length > 0` check that was always
   * false because backend endpoints don't populate `slide.images` yet.
   */
  const slidesNeedingImages = useMemo(
    () => new Set(slides.flatMap((s, i) => (componentNeedsImage(s.component) ? [i] : []))),
    [slides],
  );

  const totalSlots = useMemo(() => {
    let count = 0;
    slidesNeedingImages.forEach((i) => {
      count += componentImageCount(slides[i]?.component ?? '');
    });
    return count;
  }, [slidesNeedingImages, slides]);

  const doneSlots = useMemo(() => {
    let count = 0;
    slidesNeedingImages.forEach((i) => {
      const imgCount = componentImageCount(slides[i]?.component ?? '');
      for (let j = 0; j < imgCount; j++) {
        const state = imageSlots[slotKey(i, j)];
        if (state?.status === 'done' && state.url) count++;
      }
    });
    return count;
  }, [slidesNeedingImages, slides, imageSlots]);

  // ── Initialise virtual slots on mount / when slides change ────────────────

  const slotsInitRef = useRef(false);
  const refsInitRef = useRef(false);

  // ── Init image slots (prompts, chroma, etc.) — runs once when slides are ready.
  useEffect(() => {
    if (slides.length === 0 || slotsInitRef.current) return;
    slotsInitRef.current = true;

    const equipmentSlugs = new Set(Object.values(EQUIPMENT_SLUG_MAP));

    const logoRef: ReferenceImage | null = formData.logo_url
      ? { id: 'logo', url: formData.logo_url, source: 'logo' }
      : null;

    if (logoRef) addReferenceImage(logoRef);

    const chromaUpdates: Record<string, boolean> = {};

    slides.forEach((slide, si) => {
      if (!componentNeedsImage(slide.component)) return;

      const imgCount = componentImageCount(slide.component);
      const isProduct = slide.component.startsWith('product_');

      const slideSlugs: string[] = [];
      if (slide.product_slug) slideSlugs.push(slide.product_slug);
      if (slide.items) {
        for (const item of slide.items) {
          if (item.product_slug && !slideSlugs.includes(item.product_slug)) {
            slideSlugs.push(item.product_slug);
          }
        }
      }

      const hasEquipmentSlug = slideSlugs.some((s) => equipmentSlugs.has(s));

      for (let imgIdx = 0; imgIdx < imgCount; imgIdx++) {
        const key = slotKey(si, imgIdx);

        const existingImage = (slide.images ?? [])[imgIdx];
        const prompts = Array.isArray(promptData) ? promptData : [];
        const serverPrompt =
          prompts.find((p) => p.id === existingImage?.id)?.prompt
          ?? existingImage?.prompt
          ?? '';

        let prompt = serverPrompt || generateRawPrompt(
          slide.component, slide.title, slide.section, slide.subtitle, formData.venue,
        );

        if (logoRef) {
          prompt += ' Include the client corporate logo subtly and professionally in the composition.';
        }

        if (imgCount > 1) {
          prompt += ` (Image ${imgIdx + 1} of ${imgCount} for this slide)`;
        }

        const savedUrl = existingImage?.url || (imgIdx === 0 ? slide.image_url : undefined) || undefined;
        initImageSlot(
          key,
          {
            slideIndex: si,
            imageIndex: imgIdx,
            prompt,
            provider: 'gemini',
            needsBgRemoval: existingImage?.needs_bg_removal ?? isProduct,
          },
          savedUrl,
        );

        setSlotPrompts((prev) =>
          prev[key] !== undefined ? prev : { ...prev, [key]: prompt },
        );

        if (isProduct || hasEquipmentSlug) {
          chromaUpdates[key] = true;
        }
      }
    });

    if (Object.keys(chromaUpdates).length > 0) {
      setSlotChroma((prev) => ({ ...prev, ...chromaUpdates }));
    }

    if (selectedSlideIndex === null || !slidesNeedingImages.has(selectedSlideIndex)) {
      const first = slides.findIndex((s) => componentNeedsImage(s.component));
      if (first >= 0) setSelectedSlideIndex(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length, promptData]);

  // ── Register provider images + matching base products as default refs per slot.
  //    Runs once after both baseProducts and slides are available. Separate from
  //    slot init so it doesn't get blocked by the slotsInitRef guard.
  useEffect(() => {
    if (slides.length === 0 || !baseProducts || baseProducts.length === 0 || refsInitRef.current) return;
    refsInitRef.current = true;

    // Shared refs that go on every slot: logo + provider images.
    const sharedRefIds: string[] = [];

    if (formData.logo_url) {
      sharedRefIds.push('logo');
      addReferenceImage({ id: 'logo', url: formData.logo_url, source: 'logo' });
    }

    for (const provider of selectedProviders) {
      const providerImages = provider.images ?? (provider.image ? [provider.image] : []);
      for (let pi = 0; pi < providerImages.length; pi++) {
        const url = providerImages[pi];
        if (!url) continue;
        const refId = `provider-${provider.id}-${pi}`;
        sharedRefIds.push(refId);
        addReferenceImage({ id: refId, url, source: 'uploaded' });
      }
    }

    // Register base products that actually appear in the deck so they're
    // available in the reference pool (AddReferenceModal shows the full
    // catalog separately — here we only auto-select matching ones).
    const bpBySlug = new Map(baseProducts.map((bp) => [bp.slug, bp]));

    const refUpdates: Record<string, string[]> = {};

    slides.forEach((slide, si) => {
      if (!componentNeedsImage(slide.component)) return;
      const imgCount = componentImageCount(slide.component);

      // Collect product slugs from this slide.
      const slideSlugs: string[] = [];
      if (slide.product_slug) slideSlugs.push(slide.product_slug);
      if (slide.items) {
        for (const item of slide.items) {
          if (item.product_slug && !slideSlugs.includes(item.product_slug)) {
            slideSlugs.push(item.product_slug);
          }
        }
      }

      // Register matching base product refs for this slide's slugs.
      for (const slug of slideSlugs) {
        const bp = bpBySlug.get(slug);
        if (!bp?.base_image_url) continue;
        addReferenceImage({
          id: `base-${slug}`,
          url: bp.base_image_url,
          source: 'base_product',
          productSlug: slug,
        });
      }

      for (let imgIdx = 0; imgIdx < imgCount; imgIdx++) {
        const refs = [...sharedRefIds];

        // For multi-image slides (pair/grid), assign the matching slug per image index.
        // For single-image slides, assign all of the slide's product slugs.
        if (slideSlugs.length > 0) {
          if (imgCount > 1 && slideSlugs[imgIdx]) {
            refs.push(`base-${slideSlugs[imgIdx]}`);
          } else {
            for (const slug of slideSlugs) refs.push(`base-${slug}`);
          }
        }

        refUpdates[slotKey(si, imgIdx)] = refs;
      }
    });

    setSlotReferenceIds((prev) => ({ ...prev, ...refUpdates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length, baseProducts, selectedProviders]);

  // ── Derived state for selected slide ───────────────────────────────────────

  const selectedSlide =
    selectedSlideIndex !== null ? (slides[selectedSlideIndex] ?? null) : null;

  const slideNeedsImg =
    selectedSlide !== null && componentNeedsImage(selectedSlide.component);

  const activeImageCount = selectedSlide ? componentImageCount(selectedSlide.component) : 1;

  const activeKey =
    selectedSlideIndex !== null && slideNeedsImg
      ? slotKey(selectedSlideIndex, selectedImageIndex)
      : null;

  const activeSlotState = activeKey ? (imageSlots[activeKey] ?? null) : null;
  const activePrompt = activeKey ? (slotPrompts[activeKey] ?? '') : '';
  const activeProvider = activeKey ? (slotProviders[activeKey] ?? defaultProvider) : defaultProvider;
  const activeChroma = activeKey ? (slotChroma[activeKey] ?? false) : false;
  const activeRefIds = activeKey ? (slotReferenceIds[activeKey] ?? []) : [];

  const activeRefImages = referenceImages.filter((r) => activeRefIds.includes(r.id));

  // ── Cycle labels while active slot is generating ───────────────────────────

  useEffect(() => {
    if (activeSlotState?.status !== 'generating') {
      setImgLabel(0);
      return;
    }
    const id = setInterval(
      () => setImgLabel((s) => (s + 1) % IMAGE_GEN_LABELS.length),
      11_000,
    );
    return () => clearInterval(id);
  }, [activeSlotState?.status]);

  /**
   * Sorted array of slide indices that need images, used for prev/next nav.
   */
  const sortedImageIndices = useMemo(
    () => Array.from(slidesNeedingImages).sort((a, b) => a - b),
    [slidesNeedingImages],
  );

  const currentPosInImageSlides = selectedSlideIndex !== null
    ? sortedImageIndices.indexOf(selectedSlideIndex)
    : -1;

  const hasPrevImage =
    selectedImageIndex > 0 || currentPosInImageSlides > 0;

  const hasNextImage =
    selectedImageIndex < activeImageCount - 1 ||
    (currentPosInImageSlides >= 0 && currentPosInImageSlides < sortedImageIndices.length - 1);

  const goToPrevImage = useCallback(() => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
      return;
    }
    if (currentPosInImageSlides > 0) {
      const prevSlideIdx = sortedImageIndices[currentPosInImageSlides - 1];
      setSelectedSlideIndex(prevSlideIdx);
      const prevCount = componentImageCount(slides[prevSlideIdx]?.component ?? '');
      setSelectedImageIndex(prevCount - 1);
    }
  }, [selectedImageIndex, currentPosInImageSlides, sortedImageIndices, slides, setSelectedSlideIndex]);

  const goToNextImage = useCallback(() => {
    if (selectedImageIndex < activeImageCount - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
      return;
    }
    if (currentPosInImageSlides >= 0 && currentPosInImageSlides < sortedImageIndices.length - 1) {
      setSelectedSlideIndex(sortedImageIndices[currentPosInImageSlides + 1]);
      setSelectedImageIndex(0);
    }
  }, [selectedImageIndex, activeImageCount, currentPosInImageSlides, sortedImageIndices, setSelectedSlideIndex]);

  // ── Per-slot prompt change ─────────────────────────────────────────────────

  const handlePromptChange = useCallback(
    (value: string) => {
      if (!activeKey) return;
      setSlotPrompts((prev) => ({ ...prev, [activeKey]: value }));
    },
    [activeKey],
  );

  // ── Per-slot provider change ───────────────────────────────────────────────

  const handleProviderChange = useCallback(
    (provider: 'gemini' | 'openai') => {
      if (!activeKey) return;
      setSlotProviders((prev) => ({ ...prev, [activeKey]: provider }));
    },
    [activeKey],
  );

  // ── Per-slot chroma toggle ─────────────────────────────────────────────────

  const handleChromaToggle = useCallback(() => {
    if (!activeKey) return;
    setSlotChroma((prev) => ({ ...prev, [activeKey]: !prev[activeKey] }));
  }, [activeKey]);

  // ── Auto-save slides to backend when image URLs change ─────────────────────

  const saveSlides = useSaveSlides();
  const savePendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  const debouncedSaveSlides = useCallback(() => {
    if (!presentationId) return;
    if (savePendingRef.current) clearTimeout(savePendingRef.current);
    savePendingRef.current = setTimeout(() => {
      saveSlides.mutate({ id: presentationId, slides: slidesRef.current });
    }, 1_500);
  }, [presentationId, saveSlides]);

  useEffect(() => () => {
    if (savePendingRef.current) {
      clearTimeout(savePendingRef.current);
      if (presentationId) {
        saveSlides.mutate({ id: presentationId, slides: slidesRef.current });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Generate single slot ───────────────────────────────────────────────────

  const generateSlot = useCallback(
    async (key: string): Promise<void> => {
      if (!presentationId) return;

      const prompt = slotPrompts[key] ?? '';
      if (!prompt.trim()) return;

      const chroma = slotChroma[key] ?? false;
      const finalPrompt = chroma ? prompt + CHROMA_INSTRUCTION : prompt;
      const provider = slotProviders[key] ?? defaultProvider;
      const refIds = slotReferenceIds[key] ?? [];

      // Resolve reference IDs to actual image URLs so the backend can
      // pass them to Gemini/OpenAI as visual references.
      const refUrls = refIds
        .map((id) => referenceImages.find((r) => r.id === id)?.url)
        .filter((u): u is string => !!u);

      setImageSlotGenerating(key);

      try {
        const result = await generateImage.mutateAsync({
          id: presentationId,
          slotKey: key,
          prompt: finalPrompt,
          provider,
          referenceImageUrls: refUrls,
        });

        setImageSlotResult(key, result.url, 'done');
        debouncedSaveSlides();
      } catch (err) {
        setImageSlotResult(key, null, 'error');
        toast.error(extractApiError(err, 'Error generating image. Please try again.'), {
          duration: 8_000,
        });
      }
    },
    [
      presentationId,
      slotPrompts,
      slotChroma,
      slotProviders,
      slotReferenceIds,
      referenceImages,
      defaultProvider,
      generateImage,
      setImageSlotGenerating,
      setImageSlotResult,
      debouncedSaveSlides,
    ],
  );

  // ── Generate active slot ───────────────────────────────────────────────────

  const handleGenerateActive = useCallback(() => {
    if (!activeKey) return;
    void generateSlot(activeKey);
  }, [activeKey, generateSlot]);

  // ── Generate all idle slots ────────────────────────────────────────────────

  const handleGenerateAll = useCallback(() => {
    if (!presentationId) return;

    const tasks: Promise<void>[] = [];

    slidesNeedingImages.forEach((si) => {
      const imgCount = componentImageCount(slides[si]?.component ?? '');
      for (let j = 0; j < imgCount; j++) {
        const key = slotKey(si, j);
        const state = imageSlots[key];
        if (!state || state.status === 'idle' || state.status === 'error') {
          tasks.push(generateSlot(key));
        }
      }
    });

    if (tasks.length === 0) {
      toast('All images already generated.', { duration: 3_000 });
      return;
    }

    void Promise.allSettled(tasks).then((results) => {
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed === 0) {
        toast.success('All images generated successfully.', { duration: 3_000 });
      } else {
        toast.error(
          `${failed} image${failed !== 1 ? 's' : ''} failed. Check each slide.`,
          { duration: 8_000 },
        );
      }
    });
  }, [slidesNeedingImages, slides, imageSlots, presentationId, generateSlot]);

  // ── Background removal ─────────────────────────────────────────────────────

  const handleRemoveBg = useCallback(
    async (method: 'pil' | 'ai') => {
      if (!presentationId || !activeKey || !activeSlotState?.url) return;

      const methodKey = `${activeKey}-${method}`;
      setImageSlotGenerating(activeKey);

      try {
        const result = await removeBg.mutateAsync({
          id: presentationId,
          slotKey: methodKey,
          imageUrl: activeSlotState.url,
          method,
        });
        setImageSlotResult(activeKey, result.url, 'done');
        debouncedSaveSlides();
        toast.success('Background removed successfully.', { duration: 3_000 });
      } catch (err) {
        setImageSlotResult(activeKey, activeSlotState.url, 'done');
        toast.error(extractApiError(err, 'Could not remove background.'), { duration: 8_000 });
      }
    },
    [presentationId, activeKey, activeSlotState, removeBg, setImageSlotGenerating, setImageSlotResult, debouncedSaveSlides],
  );

  // ── Batch background removal for all chroma slots ─────────────────────────

  const chromaDoneSlots = useMemo(() => {
    const result: string[] = [];
    slidesNeedingImages.forEach((si) => {
      const imgCount = componentImageCount(slides[si]?.component ?? '');
      for (let j = 0; j < imgCount; j++) {
        const key = slotKey(si, j);
        if ((slotChroma[key]) && imageSlots[key]?.status === 'done' && imageSlots[key]?.url) {
          result.push(key);
        }
      }
    });
    return result;
  }, [slidesNeedingImages, slides, slotChroma, imageSlots]);

  const [batchBgRemoving, setBatchBgRemoving] = useState(false);

  const handleRemoveAllBg = useCallback(async () => {
    if (!presentationId || chromaDoneSlots.length === 0) return;

    setBatchBgRemoving(true);

    const tasks = chromaDoneSlots.map(async (key) => {
      const slot = imageSlots[key];
      if (!slot?.url) return;

      setImageSlotGenerating(key);
      try {
        const result = await removeBg.mutateAsync({
          id: presentationId,
          slotKey: `${key}-pil`,
          imageUrl: slot.url,
          method: 'pil',
        });
        setImageSlotResult(key, result.url, 'done');
      } catch {
        setImageSlotResult(key, slot.url, 'done');
      }
    });

    const results = await Promise.allSettled(tasks);
    const failed = results.filter((r) => r.status === 'rejected').length;
    setBatchBgRemoving(false);
    debouncedSaveSlides();

    if (failed === 0) {
      toast.success(`Background removed from ${chromaDoneSlots.length} image(s).`, { duration: 3_000 });
    } else {
      toast.error(`${failed} image(s) failed to remove background.`, { duration: 8_000 });
    }
  }, [presentationId, chromaDoneSlots, imageSlots, removeBg, setImageSlotGenerating, setImageSlotResult, debouncedSaveSlides]);

  // ── AI Assist (prompt rewrite) ─────────────────────────────────────────────

  const handlePromptAssist = useCallback(
    async (message: string): Promise<void> => {
      if (!presentationId || !activeKey) return;

      setAssistError(null);

      try {
        const currentPrompt = slotPrompts[activeKey] ?? '';
        const slideContext = selectedSlide
          ? {
              component: selectedSlide.component,
              section: selectedSlide.section,
              title: selectedSlide.title,
            }
          : undefined;

        const result = await promptAssist.mutateAsync({
          id: presentationId,
          instruction: message,
          currentPrompt,
          slideContext,
        });

        setSlotPrompts((prev) => ({ ...prev, [activeKey]: result.prompt }));
      } catch {
        const errorMsg = 'Could not improve the prompt. Please try again.';
        setAssistError(errorMsg);
        throw new Error(errorMsg);
      }
    },
    [presentationId, activeKey, selectedSlide, slotPrompts, promptAssist],
  );

  // ── Reference image management ─────────────────────────────────────────────

  const handleReferenceSelect = useCallback(
    (images: ReferenceImage[]) => {
      if (!activeKey) return;

      images.forEach((img) => addReferenceImage(img));

      setSlotReferenceIds((prev) => {
        const existing = new Set(prev[activeKey] ?? []);
        images.forEach((img) => existing.add(img.id));
        return { ...prev, [activeKey]: Array.from(existing) };
      });
    },
    [activeKey, addReferenceImage],
  );

  const handleRemoveReference = useCallback(
    (id: string) => {
      if (!activeKey) return;
      setSlotReferenceIds((prev) => ({
        ...prev,
        [activeKey]: (prev[activeKey] ?? []).filter((rid) => rid !== id),
      }));
    },
    [activeKey],
  );

  // ── Sidebar: status badge helper ───────────────────────────────────────────

  function getSlideStatus(slideIndex: number): 'idle' | 'generating' | 'done' | 'error' {
    if (!slidesNeedingImages.has(slideIndex)) return 'idle';
    const imgCount = componentImageCount(slides[slideIndex]?.component ?? '');
    let hasGenerating = false;
    let hasError = false;
    let allDone = true;
    for (let j = 0; j < imgCount; j++) {
      const state = imageSlots[slotKey(slideIndex, j)];
      if (!state || state.status === 'idle') allDone = false;
      else if (state.status === 'generating') { hasGenerating = true; allDone = false; }
      else if (state.status === 'error') { hasError = true; allDone = false; }
      else if (state.status !== 'done') allDone = false;
    }
    if (hasGenerating) return 'generating';
    if (allDone) return 'done';
    if (hasError) return 'error';
    return 'idle';
  }

  // ── Continue button ────────────────────────────────────────────────────────

  const allSlotsReady =
    totalSlots === 0 ||
    Array.from(slidesNeedingImages).every((si) => {
      const imgCount = componentImageCount(slides[si]?.component ?? '');
      for (let j = 0; j < imgCount; j++) {
        const state = imageSlots[slotKey(si, j)];
        if (state?.status !== 'done') return false;
      }
      return true;
    });


  const handleContinue = useCallback(() => {
    const updatedSlides = slides.map((slide, idx) => {
      const imgCount = componentImageCount(slide.component);
      const images = [];
      for (let j = 0; j < imgCount; j++) {
        const state = imageSlots[slotKey(idx, j)];
        if (state?.url) {
          images.push({
            id: `s${idx}-${j}`,
            url: state.url,
            prompt: state.prompt || '',
            style: 'content',
            aspect_ratio: '16:9',
          });
        }
      }
      if (images.length > 0) {
        return { ...slide, image_url: images[0].url, images };
      }
      return slide;
    });

    setSlides(updatedSlides);

    if (presentationId) {
      saveSlides.mutate(
        { id: presentationId, slides: updatedSlides },
        {
          onSuccess: () => goToBuildStep(5),
          onError: () => {
            toast.error('Error saving images. Please try again.');
          },
        },
      );
    } else {
      goToBuildStep(5);
    }
  }, [slides, imageSlots, presentationId, saveSlides, setSlides, goToBuildStep]);

  // ── Delete slide ───────────────────────────────────────────────────────────

  const handleRemoveSlide = useCallback(
    (index: number) => {
      if (slides.length <= 1) return;
      removeSlide(index);
      toast.success('Slide deleted');
    },
    [removeSlide, slides.length],
  );

  // ── Derived generated images for AddReferenceModal ────────────────────────

  const generatedImagesForModal = Object.entries(imageSlots)
    .filter(([, s]) => s.status === 'done' && s.url)
    .map(([, s]) => ({ url: s.url as string, slideIndex: s.slideIndex }));

  // ── Render ─────────────────────────────────────────────────────────────────

  const isLoadingPrompts = promptsLoading;

  return (
    <div className={styles.container}>
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className={styles.toolbar} role="toolbar" aria-label="Image generation options">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleGenerateAll}
          disabled={isLoadingPrompts || totalSlots === 0}
          aria-label="Generate all pending images"
        >
          Generate all
        </Button>

        {chromaDoneSlots.length > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRemoveAllBg}
            disabled={batchBgRemoving}
            aria-label={`Remove background from ${chromaDoneSlots.length} chroma image(s)`}
          >
            {batchBgRemoving ? 'Removing backgrounds...' : `Remove backgrounds (${chromaDoneSlots.length})`}
          </Button>
        )}

        <div className={styles.toolbarSpacer} />

        <fieldset className={styles.providerFieldset}>
          <legend className={styles.providerLegend}>Default provider</legend>
          <label className={styles.providerLabel}>
            <input
              type="radio"
              name="default-provider"
              value="gemini"
              checked={defaultProvider === 'gemini'}
              onChange={() => setDefaultProvider('gemini')}
              className={styles.providerRadio}
            />
            Gemini
          </label>
          <label className={styles.providerLabel}>
            <input
              type="radio"
              name="default-provider"
              value="openai"
              checked={defaultProvider === 'openai'}
              onChange={() => setDefaultProvider('openai')}
              className={styles.providerRadio}
            />
            OpenAI
          </label>
        </fieldset>

        <span className={styles.slotCounter} aria-live="polite" aria-atomic>
          {isLoadingPrompts ? 'Loading...' : `${doneSlots}/${totalSlots} images`}
        </span>
      </div>

      {/* ── Editor area ──────────────────────────────────────────────────── */}
      <div className={styles.editorArea}>
        {/* ── Left sidebar ─────────────────────────────────────────────── */}
        <div className={styles.sidebar} aria-label="Slides list">
          <div className={styles.sidebarHeader}>
            <h2 className={styles.sidebarHeaderTitle}>Slides</h2>
          </div>

          {isLoadingPrompts ? (
            <div className={styles.sidebarSkeletonList} aria-label="Loading..." aria-busy role="status">
              {Array.from({ length: SIDEBAR_SKELETON_COUNT }, (_, i) => (
                <div
                  key={i}
                  className={styles.sidebarSkeletonBar}
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
              <p className={styles.loadingMessage}>Preparing image prompts...</p>
            </div>
          ) : (
            <div className={styles.slideList} role="list">
              {slides.map((slide, i) => {
                const needsImg = componentNeedsImage(slide.component);
                const status = needsImg ? getSlideStatus(i) : 'idle';
                const isActiveSlide = selectedSlideIndex === i;

                return (
                  <div
                    key={i}
                    role="listitem"
                    className={[
                      styles.slideCard,
                      isActiveSlide ? styles.slideCardSelected : '',
                      !needsImg ? styles.slideCardDimmed : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => { setSelectedSlideIndex(i); setSelectedImageIndex(0); }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({ x: e.clientX, y: e.clientY, slideIndex: i });
                    }}
                    aria-label={`Slide ${i + 1}: ${slide.title || slide.component}`}
                    aria-selected={isActiveSlide}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedSlideIndex(i);
                        setSelectedImageIndex(0);
                      }
                    }}
                  >
                    <SlideMockPreview
                      component={slide.component}
                      plantillaBgUrl={getPlantillaBgUrl(slide, i)}
                      title={slide.title}
                      section={slide.section}
                      slideNumber={i + 1}
                      isSelected={isActiveSlide}
                    />

                    {needsImg && (
                      <span
                        className={[
                          styles.statusBadge,
                          styles[`statusBadge--${status}`],
                        ].join(' ')}
                        aria-label={`Status: ${status}`}
                      >
                        {status === 'generating' && (
                          <span className={styles.statusDot} aria-hidden />
                        )}
                        {status === 'done' && <span aria-hidden>&#10003;</span>}
                        {status === 'error' && <span aria-hidden>!</span>}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Center panel ──────────────────────────────────────────────── */}
        <div className={styles.centerPanel} aria-label="Image generation panel">
          {selectedSlide === null ? (
            <div className={styles.emptyCenter}>
              <p>Select a slide from the left.</p>
            </div>
          ) : !slideNeedsImg ? (
            <div className={styles.noImagesCenter}>
              <p className={styles.noImagesTitle}>
                This slide ({getComponentLabel(selectedSlide.component)}) does not require images.
              </p>
              <p className={styles.noImagesHint}>
                Divider, statistics, timeline and cost table components do not use generated images.
              </p>
            </div>
          ) : (
            <div className={styles.panelBody}>
              {/* ── Slide header ──────────────────────────────────────────── */}
              <div className={styles.panelHeader}>
                <div className={styles.panelHeaderCenter}>
                  <p className={styles.panelSlideTitle}>
                    Slide {(selectedSlideIndex ?? 0) + 1}:{' '}
                    <strong>{getComponentLabel(selectedSlide.component)}</strong>
                    {selectedSlide.section && (
                      <span style={{ fontWeight: 400, opacity: 0.6 }}>
                        {' '}
                        — {selectedSlide.section}
                      </span>
                    )}
                  </p>
                  <p className={styles.panelSlideInfo}>
                    Style: {getImageStyle(selectedSlide.component)}
                    {' · '}
                    Aspect: {getImageAspectRatio(selectedSlide.component)}
                    {' · '}
                    Image {selectedImageIndex + 1}
                    {activeImageCount > 1 ? `/${activeImageCount}` : ''}
                    {' · '}
                    Slide {currentPosInImageSlides + 1}/{sortedImageIndices.length}
                  </p>
                </div>

                {activeImageCount > 1 && (
                  <div className={styles.imageTabsRow}>
                    {Array.from({ length: activeImageCount }, (_, imgIdx) => {
                      const tabKey = slotKey(selectedSlideIndex!, imgIdx);
                      const tabState = imageSlots[tabKey];
                      const isDone = tabState?.status === 'done' && tabState.url;
                      return (
                        <button
                          key={imgIdx}
                          type="button"
                          className={`${styles.imageTab} ${imgIdx === selectedImageIndex ? styles.imageTabActive : ''}`}
                          onClick={() => setSelectedImageIndex(imgIdx)}
                        >
                          Imagen {imgIdx + 1} {isDone ? '✓' : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Nav arrows + controls container ──────────────────────── */}
              <div className={styles.navControlsRow}>
                <button
                  type="button"
                  className={styles.navArrowTall}
                  disabled={!hasPrevImage}
                  onClick={goToPrevImage}
                  aria-label="Previous image"
                >
                  ‹
                </button>

                <div className={styles.controlsCol}>
                  {/* ── Prompt textarea ─────────────────────────────────── */}
                  <div className={styles.promptSection}>
                    <label className={styles.sectionLabel} htmlFor="img-prompt">
                      Image prompt
                    </label>
                    <textarea
                      id="img-prompt"
                      className={styles.promptTextarea}
                      value={activePrompt}
                      onChange={(e) => handlePromptChange(e.target.value)}
                      rows={4}
                      disabled={activeSlotState?.status === 'generating'}
                      aria-label="Edit image prompt"
                    />
                  </div>

                  {/* ── Reference images strip ──────────────────────────── */}
                  <div className={styles.refsSection}>
                    <span className={styles.sectionLabel}>References</span>
                    <div className={styles.refsRow}>
                      {activeRefImages.map((ref) => (
                        <div key={ref.id} className={styles.refChip}>
                          <img
                            src={ref.url}
                            alt={ref.source}
                            className={styles.refChipImg}
                          />
                          <button
                            type="button"
                            className={styles.refChipRemove}
                            onClick={() => handleRemoveReference(ref.id)}
                            aria-label={`Remove reference ${ref.source}`}
                          >
                            &#10005;
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className={styles.addRefButton}
                        onClick={() => setRefModalOpen(true)}
                        aria-label="Add reference image"
                      >
                        + Add reference
                      </button>
                    </div>
                  </div>

                  {/* ── AI Assist ───────────────────────────────────────── */}
                  <div className={styles.assistSection}>
                    <span className={styles.sectionLabel}>Adjust prompt</span>
                    <AiAssistInput
                      placeholder="Make it more minimalist, add people, change the style..."
                      onSubmit={handlePromptAssist}
                      isLoading={promptAssist.isPending}
                      error={assistError}
                      onClearError={() => setAssistError(null)}
                    />
                  </div>

                  {/* ── Options row ─────────────────────────────────────── */}
                  <div className={styles.optionsRow}>
                    <label className={styles.chromaLabel}>
                      <input
                        type="checkbox"
                        className={styles.chromaCheckbox}
                        checked={activeChroma}
                        onChange={handleChromaToggle}
                        aria-label="Chroma background (green screen)"
                      />
                      Chroma background
                    </label>

                    <fieldset className={styles.providerFieldset}>
                      <legend className={styles.providerLegend}>Provider</legend>
                      <label className={styles.providerLabel}>
                        <input
                          type="radio"
                          name={`slot-provider-${activeKey ?? 'none'}`}
                          value="gemini"
                          checked={activeProvider === 'gemini'}
                          onChange={() => handleProviderChange('gemini')}
                          className={styles.providerRadio}
                        />
                        Gemini
                      </label>
                      <label className={styles.providerLabel}>
                        <input
                          type="radio"
                          name={`slot-provider-${activeKey ?? 'none'}`}
                          value="openai"
                          checked={activeProvider === 'openai'}
                          onChange={() => handleProviderChange('openai')}
                          className={styles.providerRadio}
                        />
                        OpenAI
                      </label>
                    </fieldset>
                  </div>

                  {/* ── Generate / Regenerate + Background removal ──────── */}
                  <div className={styles.generateRow}>
                    <Button
                      variant="primary"
                      size="md"
                      onClick={handleGenerateActive}
                      disabled={
                        !activeKey ||
                        !activePrompt.trim() ||
                        activeSlotState?.status === 'generating'
                      }
                      aria-label={
                        activeSlotState?.url ? 'Regenerate image' : 'Generate image'
                      }
                    >
                      {activeSlotState?.status === 'generating'
                        ? 'Generating...'
                        : activeSlotState?.url
                          ? 'Regenerate'
                          : 'Generate'}
                    </Button>

                    {activeSlotState?.url && activeSlotState.status === 'done' && (
                      <>
                        <Button
                          variant="secondary"
                          size="md"
                          onClick={() => void handleRemoveBg('pil')}
                          disabled={removeBg.isPending}
                          aria-label="Remove background with PIL (fast)"
                        >
                          {removeBg.isPending ? 'Removing...' : 'Remove background (fast)'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="md"
                          onClick={() => void handleRemoveBg('ai')}
                          disabled={removeBg.isPending}
                          aria-label="Remove background with AI (better quality)"
                        >
                          {removeBg.isPending ? 'Removing...' : 'Remove background (AI)'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.navArrowTall}
                  disabled={!hasNextImage}
                  onClick={goToNextImage}
                  aria-label="Next image"
                >
                  ›
                </button>
              </div>

              {/* ── Preview area ──────────────────────────────────────────── */}
              <div className={styles.previewSection}>
                <span className={styles.sectionLabel}>Preview</span>
                <div className={styles.previewBox} aria-label="Generated image preview">
                  {activeSlotState?.status === 'generating' ? (
                    <div className={styles.imgGenCard} aria-label="Generating image" role="status">
                      <div className={styles.imgGenShimmer} aria-hidden />
                      <div className={styles.imgGenDot} aria-hidden />
                      <p className={styles.imgGenLabel}>{IMAGE_GEN_LABELS[imgLabel]}</p>
                      <div className={styles.imgGenBar}>
                        <div className={styles.imgGenBarFill} />
                      </div>
                    </div>
                  ) : activeSlotState?.url ? (
                    <img
                      src={activeSlotState.url}
                      alt="Generated image"
                      className={styles.previewImg}
                    />
                  ) : (
                    <div className={styles.previewEmpty}>
                      <p>Generate the image to see a preview.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div className={styles.footerRow}>
        <Button
          variant="primary"
          size="md"
          onClick={handleContinue}
          disabled={(!allSlotsReady && totalSlots > 0) || saveSlides.isPending}
          aria-label="Continue to costs step"
        >
          {saveSlides.isPending ? 'Saving...' : 'Continue to costs →'}
        </Button>
      </div>

      {/* ── Right-click context menu ──────────────────────────────────────── */}
      {contextMenu !== null && (
        <div
          className={styles.contextMenu}
          style={{ top: contextMenu.y, left: contextMenu.x }}
          role="menu"
          aria-label="Slide options"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className={styles.contextMenuItem}
            onClick={() => {
              setLightboxIndex(contextMenu.slideIndex);
              setContextMenu(null);
            }}
          >
            View macro
          </button>
          <button
            type="button"
            role="menuitem"
            className={`${styles.contextMenuItem} ${styles.contextMenuItemDanger}`}
            onClick={() => {
              setConfirmDeleteIndex(contextMenu.slideIndex);
              setContextMenu(null);
            }}
            disabled={slides.length <= 1}
          >
            Delete slide
          </button>
        </div>
      )}

      {/* ── Confirm delete modal ─────────────────────────────────────────── */}
      {confirmDeleteIndex !== null && (
        <div
          className={styles.confirmOverlay}
          onClick={() => setConfirmDeleteIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Confirm deletion"
        >
          <div className={styles.confirmCard} onClick={(e) => e.stopPropagation()}>
            <p className={styles.confirmText}>
              Delete slide {confirmDeleteIndex + 1}?
            </p>
            <p className={styles.confirmSubtext}>
              This action cannot be undone.
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmCancelBtn}
                onClick={() => setConfirmDeleteIndex(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmDeleteBtn}
                onClick={() => {
                  handleRemoveSlide(confirmDeleteIndex);
                  setConfirmDeleteIndex(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Slide macro lightbox ─────────────────────────────────────────── */}
      <SlideLightbox
        slides={slides}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        getPlantillaBgUrl={getPlantillaBgUrl}
        imageSlots={imageSlots}
      />

      {/* ── AddReferenceModal ─────────────────────────────────────────────── */}
      <AddReferenceModal
        isOpen={refModalOpen}
        onClose={() => setRefModalOpen(false)}
        onSelect={handleReferenceSelect}
        logoUrl={formData.logo_url}
        generatedImages={generatedImagesForModal}
      />
    </div>
  );
};
